/**
 * Cut-length measurement for laser-cutting perimeter pricing.
 * Primary: Clipper2 vector pipeline (flatten, clip intersect, sum all contours) via vectorCutLength.ts.
 * Fallback: raster silhouette for soft masks, filters, embedded raster, or failed vector results.
 *
 * Text glyph paths: build a display SVG (see buildTextDisplaySvg) with pathData
 * already in viewBox space (offset baked at layout); measure with
 * measureFullSvgDocumentPerimeter; scale via sourceUnitsToMm using width / viewBoxWidth.
 *
 * Trim / selection width (viewBoxWidth): axis-aligned bounds in root user space
 * via local getBBox() + stroke pad + full ancestor transform. Embedded <text> is included.
 */

import { parseSvgViewBox } from './svgDocument';
import { isPlausibleCutLength, profileSvgForMeasurement } from './svgMeasureProfile';
import { expandBBoxForStroke } from './svgStrokeBounds';
import { measureVectorCutLengthFromSvg } from './vectorCutLength';

const GEOMETRY_SELECTOR = 'path,circle,ellipse,rect,line,polyline,polygon';
/** Bounds include text labels embedded in uploaded SVGs. */
const BOUNDS_SELECTOR = `${GEOMETRY_SELECTOR},text`;
const MEASURE_TARGET_PX = 240;
const ALPHA_THRESHOLD = 127;
const MEASURE_PADDING_PX = 2;

/** Off-screen mount for DOM measurement. Do not use visibility:hidden — it inherits to
 *  child geometry and makes isCutVisible() reject every path. Do not use opacity:0 for
 *  the same reason (opacity check in isCutVisible). */
const MEASURE_MOUNT_STYLES =
  'position:absolute;left:-9999px;top:-9999px;overflow:hidden;pointer-events:none';

type SvgMatrixLike = { a: number; b: number; c: number; d: number; e: number; f: number };

const NON_RENDERED_ANCESTOR_TAGS = new Set(['defs', 'clippath', 'mask', 'symbol', 'metadata', 'title', 'desc']);

function hasPaintedFill(computed: CSSStyleDeclaration): boolean {
  const fill = computed.fill;
  if (!fill || fill === 'none' || fill === 'transparent') return false;
  const opacity = parseFloat(computed.fillOpacity);
  return !Number.isFinite(opacity) || opacity > 0;
}

function hasPaintedStroke(computed: CSSStyleDeclaration): boolean {
  const stroke = computed.stroke;
  if (!stroke || stroke === 'none' || stroke === 'transparent') return false;
  const opacity = parseFloat(computed.strokeOpacity);
  return !Number.isFinite(opacity) || opacity > 0;
}

function isCutVisible(computed: CSSStyleDeclaration): boolean {
  if (computed.display === 'none' || computed.visibility === 'hidden') return false;
  const opacity = parseFloat(computed.opacity);
  return !Number.isFinite(opacity) || opacity > 0;
}

function isMeasurableGeometry(element: Element, root: SVGSVGElement): boolean {
  let node: Element | null = element;
  while (node && node !== root) {
    if (NON_RENDERED_ANCESTOR_TAGS.has(node.tagName.toLowerCase())) return false;
    node = node.parentElement;
  }
  return true;
}

type SvgTransformTarget = SVGGraphicsElement & {
  getTransformToElement(target: SVGElement): SvgMatrixLike;
};

function supportsTransformToElement(geom: SVGGraphicsElement): geom is SvgTransformTarget {
  return typeof (geom as SvgTransformTarget).getTransformToElement === 'function';
}

/** Matrix from geometry local space → root <svg> user space (full ancestor chain). */
function getGeometryToRootMatrix(
  geom: SVGGraphicsElement,
  root: SVGSVGElement
): SvgMatrixLike | null {
  try {
    if (supportsTransformToElement(geom)) {
      return geom.getTransformToElement(root);
    }
    const geomCtm = geom.getCTM();
    const rootCtm = root.getCTM();
    if (geomCtm && rootCtm) {
      return rootCtm.inverse().multiply(geomCtm);
    }
  } catch {
    // thrown when nodes are not in the same tree
  }
  return geom.getCTM();
}

function isCutGeometry(geom: SVGGraphicsElement, root: SVGSVGElement): boolean {
  if (!isMeasurableGeometry(geom, root)) return false;

  const computed = getComputedStyle(geom);
  if (!isCutVisible(computed)) return false;

  const hasFill = hasPaintedFill(computed);
  const hasStroke = hasPaintedStroke(computed);
  return hasFill || hasStroke;
}

function transformPoint(matrix: SvgMatrixLike, x: number, y: number): { x: number; y: number } {
  return {
    x: matrix.a * x + matrix.c * y + matrix.e,
    y: matrix.b * x + matrix.d * y + matrix.f,
  };
}

type BoundsAccumulator = { minX: number; minY: number; maxX: number; maxY: number };

export type SvgContentBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Axis-aligned selection bounds for one element in root user space. */
function selectionBoundsForGeometry(
  geom: SVGGraphicsElement,
  root: SVGSVGElement
): SvgContentBounds | null {
  let bbox: DOMRect;
  try {
    bbox = geom.getBBox();
  } catch {
    return null;
  }

  const computed = getComputedStyle(geom);
  bbox = expandBBoxForStroke(geom, bbox, computed, hasPaintedStroke(computed));
  if (!(bbox.width > 0) || !(bbox.height > 0)) return null;

  const matrix = getGeometryToRootMatrix(geom, root);
  if (!matrix) return null;

  const acc = includeTransformedBBox(null, matrix, bbox);
  if (!acc || !(acc.maxX > acc.minX) || !(acc.maxY > acc.minY)) return null;

  return {
    x: acc.minX,
    y: acc.minY,
    width: acc.maxX - acc.minX,
    height: acc.maxY - acc.minY,
  };
}

function includeTransformedBBox(
  acc: BoundsAccumulator | null,
  matrix: SvgMatrixLike,
  bbox: DOMRect
): BoundsAccumulator | null {
  const corners = [
    transformPoint(matrix, bbox.x, bbox.y),
    transformPoint(matrix, bbox.x + bbox.width, bbox.y),
    transformPoint(matrix, bbox.x, bbox.y + bbox.height),
    transformPoint(matrix, bbox.x + bbox.width, bbox.y + bbox.height),
  ];

  let next = acc;
  for (const { x, y } of corners) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (!next) {
      next = { minX: x, minY: y, maxX: x, maxY: y };
    } else {
      next.minX = Math.min(next.minX, x);
      next.minY = Math.min(next.minY, y);
      next.maxX = Math.max(next.maxX, x);
      next.maxY = Math.max(next.maxY, y);
    }
  }
  return next;
}

/** Union of cut-geometry selection bboxes in root SVG user space. */
function sumVectorContentBoundsInMountedSvg(root: SVGSVGElement): SvgContentBounds | null {
  let acc: BoundsAccumulator | null = null;

  const elements = root.querySelectorAll(BOUNDS_SELECTOR);
  for (let i = 0; i < elements.length; i += 1) {
    const element = elements[i];
    if (!(element instanceof SVGGraphicsElement)) continue;
    if (!isCutGeometry(element, root)) continue;

    const bounds = selectionBoundsForGeometry(element, root);
    if (!bounds) continue;

    acc = unionBounds(acc, bounds);
  }

  if (!acc || !(acc.maxX > acc.minX) || !(acc.maxY > acc.minY)) return null;

  return {
    x: acc.minX,
    y: acc.minY,
    width: acc.maxX - acc.minX,
    height: acc.maxY - acc.minY,
  };
}

function unionBounds(
  acc: BoundsAccumulator | null,
  bounds: SvgContentBounds
): BoundsAccumulator | null {
  const { x, y, width, height } = bounds;
  if (!(width > 0) || !(height > 0)) return acc;

  const minX = x;
  const minY = y;
  const maxX = x + width;
  const maxY = y + height;

  if (!acc) {
    return { minX, minY, maxX, maxY };
  }

  return {
    minX: Math.min(acc.minX, minX),
    minY: Math.min(acc.minY, minY),
    maxX: Math.max(acc.maxX, maxX),
    maxY: Math.max(acc.maxY, maxY),
  };
}

function withMountedSvgRoot<T>(doc: Document, measure: (root: SVGSVGElement) => T): T | null {
  const parsedRoot = doc.documentElement;
  if (!parsedRoot || parsedRoot.nodeName.toLowerCase() !== 'svg') return null;

  const root = parsedRoot.cloneNode(true) as SVGSVGElement;
  root.style.cssText = MEASURE_MOUNT_STYLES;

  document.body.appendChild(root);

  try {
    return measure(root);
  } finally {
    document.body.removeChild(root);
  }
}

function countAlphaPerimeter(data: Uint8ClampedArray, width: number, height: number): number {
  let perimeter = 0;
  const alphaAt = (x: number, y: number) => data[(y * width + x) * 4 + 3];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (alphaAt(x, y) <= ALPHA_THRESHOLD) continue;
      if (
        x === 0 ||
        x === width - 1 ||
        y === 0 ||
        y === height - 1 ||
        alphaAt(x - 1, y) <= ALPHA_THRESHOLD ||
        alphaAt(x + 1, y) <= ALPHA_THRESHOLD ||
        alphaAt(x, y - 1) <= ALPHA_THRESHOLD ||
        alphaAt(x, y + 1) <= ALPHA_THRESHOLD
      ) {
        perimeter += 1;
      }
    }
  }

  return perimeter;
}

function makeMeasureCanvas(sourceWidth: number, sourceHeight: number) {
  if (sourceWidth <= 0 || sourceHeight <= 0) return null;

  const scale = MEASURE_TARGET_PX / Math.max(sourceWidth, sourceHeight);
  const width = Math.max(4, Math.ceil(sourceWidth * scale) + MEASURE_PADDING_PX * 2);
  const height = Math.max(4, Math.ceil(sourceHeight * scale) + MEASURE_PADDING_PX * 2);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  return { ctx, scale, width, height };
}

function loadMeasureImage(
  url: string,
  onLoad: (img: HTMLImageElement) => void,
  onError: () => void
): HTMLImageElement {
  const img = new Image();
  if (shouldUseCrossOrigin(url)) {
    img.crossOrigin = 'anonymous';
  }
  img.onload = () => onLoad(img);
  img.onerror = onError;
  return img;
}

function shouldUseCrossOrigin(url: string): boolean {
  if (url.startsWith('blob:') || url.startsWith('data:')) return false;
  try {
    const resolved = new URL(url, window.location.href);
    return resolved.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/** Raster silhouette perimeter in viewBox units (fallback when no SVG paths exist). */
export function measureRasterSilhouettePerimeter(
  url: string,
  viewBoxWidth: number,
  viewBoxHeight: number
): Promise<number> {
  if (!url) return Promise.resolve(0);

  const measure = makeMeasureCanvas(viewBoxWidth, viewBoxHeight);
  if (!measure) return Promise.resolve(0);

  const { ctx, scale, width, height } = measure;
  const innerWidth = width - MEASURE_PADDING_PX * 2;
  const innerHeight = height - MEASURE_PADDING_PX * 2;

  return new Promise((resolve) => {
    const img = loadMeasureImage(
      url,
      (loaded) => {
        try {
          ctx.drawImage(loaded, MEASURE_PADDING_PX, MEASURE_PADDING_PX, innerWidth, innerHeight);
          const perimeterPx = countAlphaPerimeter(ctx.getImageData(0, 0, width, height).data, width, height);
          resolve(perimeterPx / scale);
        } catch {
          resolve(0);
        }
      },
      () => resolve(0)
    );
    img.src = url;
  });
}

function measureSvgBlobSilhouettePerimeter(
  svgString: string,
  viewBoxWidth: number,
  viewBoxHeight: number
): Promise<number> {
  const measure = makeMeasureCanvas(viewBoxWidth, viewBoxHeight);
  if (!measure) return Promise.resolve(0);

  const innerWidth = measure.width - MEASURE_PADDING_PX * 2;
  const innerHeight = measure.height - MEASURE_PADDING_PX * 2;
  const blobUrl = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' }));

  return new Promise((resolve) => {
    const img = loadMeasureImage(
      blobUrl,
      (loaded) => {
        try {
          measure.ctx.drawImage(loaded, MEASURE_PADDING_PX, MEASURE_PADDING_PX, innerWidth, innerHeight);
          const perimeterPx = countAlphaPerimeter(
            measure.ctx.getImageData(0, 0, measure.width, measure.height).data,
            measure.width,
            measure.height
          );
          resolve(perimeterPx / measure.scale);
        } catch {
          resolve(0);
        } finally {
          URL.revokeObjectURL(blobUrl);
        }
      },
      () => {
        URL.revokeObjectURL(blobUrl);
        resolve(0);
      }
    );
    img.src = blobUrl;
  });
}

/** Tight bounds of cut geometry in a complete SVG document (transform-aware, vector). */
export function measureFullSvgDocumentContentBounds(
  rawSvg: string,
  _fallbackWidth: number,
  _fallbackHeight: number
): SvgContentBounds | null {
  if (!rawSvg?.trim()) return null;

  try {
    const doc = new DOMParser().parseFromString(rawSvg, 'image/svg+xml');
    if (!doc.querySelector('parsererror')) {
      return withMountedSvgRoot(doc, sumVectorContentBoundsInMountedSvg);
    }
  } catch {
    // no vector bounds available
  }

  return null;
}

/**
 * Cut-length measure: Clipper2 vector (union + hard-clip intersect) with raster-silhouette
 * fallback for soft masks, filters, embedded raster, or failed vector results.
 */
export async function measureFullSvgDocumentPerimeter(
  rawSvg: string,
  fallbackWidth: number,
  fallbackHeight: number
): Promise<number> {
  const viewBox = parseSvgViewBox(rawSvg, fallbackWidth, fallbackHeight);
  const profile = profileSvgForMeasurement(rawSvg);

  if (profile.strategy === 'raster-silhouette') {
    return measureSvgBlobSilhouettePerimeter(rawSvg, viewBox.width, viewBox.height);
  }

  const vectorLength = await measureVectorCutLengthFromSvg(rawSvg, viewBox.width, viewBox.height);
  if (isPlausibleCutLength(vectorLength, viewBox.width, viewBox.height)) {
    return vectorLength;
  }

  return measureSvgBlobSilhouettePerimeter(rawSvg, viewBox.width, viewBox.height);
}
